import NiceModal, { useModal } from "@ebay/nice-modal-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "utils/marketplaceConstants";
import { css } from "../../../styled-system/css";
import { Flex, HStack, Wrap } from "../../../styled-system/jsx";
import { center, vstack } from "../../../styled-system/patterns";
import { HoverInfo } from "../../components/core/basic/Tooltip";
import { Modal } from "../../components/core/basic/RACModal";
import { Datepicker } from "../../components/fields/base/Datepicker";
import { NumberInput } from "../../components/fields/base/NumberInput";
import { RadioGroup } from "../../components/fields/base/RadioGroup";
import { Select } from "../../components/fields/base/Select";
import { Button } from "../../components/core/basic/Button";
import { formatCurrency } from "../../utils/formatNumber";
import { Checkbox } from "components/fields/base/Checkbox";
import { Label } from "react-aria-components";
import { inputClassNames } from "components/fields/styles/styles";

export interface BulkAdPlanActionFormData {
  actionType: string | null;
  actionValue?: number;
  actionUnit?: string;
  startDate?: string;
  endDate?: string;
  biddingProgram?: string;
  keepBoostedBid?: boolean;
}

interface Props {
  formData: BulkAdPlanActionFormData;
  setFormData: (data: BulkAdPlanActionFormData) => void;
  actionTarget: string;
  hidePercentOption?: boolean;
  hideCurrencyOption?: boolean;
  hideActionTypeOptions?: boolean;
  forcePercentOption?: boolean;
  forceCurrencyOption?: boolean;
  schedulingDialogue?: boolean;
  renderDateFields?: boolean;
  renderKeepBidBoostField?: boolean;
  disabledFields?: string[];
}

const BulkAdPlanActionForm = ({
  formData,
  setFormData,
  actionTarget,
  hidePercentOption,
  hideCurrencyOption,
  hideActionTypeOptions,
  forcePercentOption,
  forceCurrencyOption = false,
  schedulingDialogue,
  renderDateFields,
  disabledFields = [],
  renderKeepBidBoostField = false,
}: Props) => {
  const handleChange = <T extends keyof BulkAdPlanActionFormData>(key: T, value: BulkAdPlanActionFormData[T]) => {
    setFormData({ ...formData, [key]: value });
  };

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const classNames = inputClassNames();

  useEffect(() => {
    if (forcePercentOption) {
      setFormData({ ...formData, actionUnit: "percent" });
    }
    if (forceCurrencyOption) {
      setFormData({ ...formData, actionUnit: "currency" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcePercentOption, forceCurrencyOption]);

  const actionTypeOptions = [
    { label: `Specify ${actionTarget}`, value: "specify" },
    { label: `Increase ${actionTarget}`, value: "increase" },
    { label: `Decrease ${actionTarget}`, value: "decrease" },
  ];

  const { actionType, actionUnit, startDate, endDate } = formData;

  return (
    <form className={vstack({ alignItems: "stretch", width: "100%", paddingX: "xlarge" })}>
      <p className={css({ textAlign: "center" })}>
        Please specify changes to your {actionTarget.toLowerCase().replace("acos", "ACOS").replace("roas", "ROAS")}{" "}
        using the fields below.
      </p>
      <Wrap gap="xlarge" justify={"stretch"} width={"100%"}>
        {!hideActionTypeOptions && (
          <Select
            label={"Action Type"}
            onSelectionChange={(value) => {
              handleChange("actionType", value as string);
            }}
            selectedKey={actionType}
            items={schedulingDialogue ? actionTypeOptions.filter((o) => o.value !== "specify") : actionTypeOptions}
            isDisabled={disabledFields.includes("actionType")}
            className={css({
              flex: "2",
            })}
          />
        )}
        <NumberInput
          onChange={(value) => handleChange("actionValue", value)}
          value={formData.actionValue}
          label={"Amount"}
          placeholder={"Enter amount"}
          className={css({
            flex: "2",
          })}
          isDisabled={disabledFields.includes("actionValue")}
          minValue={0}
        />
        {!(hideCurrencyOption && hidePercentOption) && (
          <RadioGroup
            className={css({
              flex: "1",
            })}
            orientation={"horizontal"}
            label="Change Unit"
            isDisabled={disabledFields.includes("actionUnit")}
            items={[
              hideCurrencyOption ? null : (
                {
                  value: "currency",
                  label: MARKETPLACE_CURRENCY_DENOMINATION[marketPlace] ?? MARKETPLACE_CURRENCY_DENOMINATION["USA"]!,
                }
              ),
              hidePercentOption ? null : (
                {
                  value: "percent",
                  label: "%",
                }
              ),
            ].filter(Boolean)}
            value={actionUnit}
            onChange={(value) => handleChange("actionUnit", value)}
          />
        )}
      </Wrap>
      {(schedulingDialogue || renderDateFields) && (
        <Wrap gap="xlarge">
          <Datepicker
            label={
              <>
                Start Date <HoverInfo>Changes will occur at the beginning of the specified day</HoverInfo>
              </>
            }
            value={startDate}
            onChange={(value) => handleChange("startDate", value as string)}
            disabled={disabledFields.includes("startDate")}
            minDate={moment().toDate()}
            className={css({ flex: "2" })}
            inputClassName={css({ width: "100%" })}
            ignoreUtc={true}
          />
          <Datepicker
            label={
              <>
                End Date <HoverInfo>Changes will occur at the end of the specified day</HoverInfo>
              </>
            }
            value={endDate}
            onChange={(value) => handleChange("endDate", value as string)}
            disabled={disabledFields.includes("endDate")}
            minDate={moment(startDate).toDate()}
            className={css({ flex: "2" })}
            inputClassName={css({ width: "100%" })}
            ignoreUtc={true}
          />
          {!(hideCurrencyOption && hidePercentOption) && <div className={css({ flex: "1" })} />}
          {renderKeepBidBoostField && (
            <Wrap justify={"flex-end"} align={"center"} paddingTop={"large"}>
              <Checkbox
                value={formData.keepBoostedBid ?? false}
                onChange={(value) => {
                  handleChange("keepBoostedBid", value);
                }}
              />
              <Label className={classNames.label}>
                <Flex gap={"small"}>
                  Keep {actionTarget === "bid reduction" ? "Reduced" : "Boosted"} Bid After End Date{" "}
                  <HoverInfo>
                    By default the bid will be reverted to it's value before the{" "}
                    {actionTarget === "bid reduction" ? "reduction" : "boost"} period. Select this option to keep the
                    bid value at the end of the bid {actionTarget === "bid reduction" ? "reduction" : "boost"}.
                  </HoverInfo>
                </Flex>
              </Label>
            </Wrap>
          )}
        </Wrap>
      )}
    </form>
  );
};

export default BulkAdPlanActionForm;

interface ModalProps
  extends Omit<Props, "formData" | "setFormData" | "hidePercentOption" | "hideCurrencyOption" | "forcePercentOption"> {
  title?: string;
  initialValues?: BulkAdPlanActionFormData;
  onConfirm: (data: BulkAdPlanActionFormData) => void;
  actionTarget: string;
  unitAvailability?: "dynamic" | "alwaysPercent" | "none";
}

export const BulkAdPlanActionModal = NiceModal.create(
  ({
    title = "Confirm Bulk Action",
    unitAvailability = "dynamic",
    initialValues = {
      actionType: null,
      actionValue: 0,
      actionUnit: unitAvailability === "dynamic" ? "currency" : "percent",
      startDate: moment().toISOString().split("T")[0]!,
      endDate: moment().add(1, "day").toISOString().split("T")[0]!,
      keepBoostedBid: false,
    },
    onConfirm,
    ...props
  }: ModalProps) => {
    const modal = useModal();
    const [formData, setFormData] = useState<BulkAdPlanActionFormData>(initialValues);
    const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

    const isValid =
      !!formData.actionType &&
      formData.actionValue &&
      formData.actionValue > 0 &&
      !!formData.actionUnit &&
      ((!props.schedulingDialogue && !props.renderDateFields) || (!!formData.startDate && !!formData.endDate));

    const isDateValid = moment(formData.startDate).isSameOrBefore(moment(formData.endDate));

    return (
      <Modal
        isOpen={modal.visible}
        isDismissable
        onOpenChange={(isOpen) => (isOpen ? void 0 : modal.remove())}
        title={title}
        className={css({ minWidth: "60vw" })}
      >
        <BulkAdPlanActionForm
          formData={formData}
          setFormData={setFormData}
          hidePercentOption={
            (unitAvailability === "dynamic" && formData.actionType === "specify") || unitAvailability === "none"
          }
          hideCurrencyOption={unitAvailability === "alwaysPercent" || unitAvailability === "none"}
          {...props}
        />
        {!isValid && <p className={center({ margin: 0 })}>Please fill in required fields</p>}
        {props.schedulingDialogue && !isDateValid && (
          <p className={center({ margin: 0 })}>Start date must be before end date</p>
        )}
        {isValid && props.schedulingDialogue && (
          <p
            className={center({ margin: 0, paddingX: "xlarge" })}
          >{`Are you sure you want to ${formData.actionType} the ${props.actionTarget.toLowerCase().replace("acos", "ACOS").replace("roas", "ROAS")} by ${
            formData.actionUnit === "percent" ?
              `${Math.round(Math.abs(formData.actionValue as number))}%`
            : formatCurrency(Math.abs(formData.actionValue as number), marketPlace)
          } for the selected media plans from ${moment(formData.startDate).format("MMMM D")} to ${moment(
            formData.endDate,
          ).format("MMMM D")}  (${(moment(formData.endDate).diff(formData.startDate, "days") + 1) * 24} hours)?`}</p>
        )}
        <HStack justifyContent="space-around">
          <Button
            variant={"secondary"}
            onPress={() => {
              modal.remove();
            }}
          >
            Cancel
          </Button>

          <Button
            onPress={() => {
              onConfirm(formData);
              modal.remove();
            }}
            isDisabled={!isValid || !isDateValid}
          >
            Confirm
          </Button>
        </HStack>
      </Modal>
    );
  },
);
